import createClient from 'openapi-fetch';

import { getPreviousUrl } from './previousUrl';
import type { paths } from './types/generated';
import { plenaryIdentity } from './utils/plenaryIdentity';
import { PLENARY_CONTEXT_ID_HEADER } from '../common/consts/plenaryIdHeader';
import { getEnvVar } from '../utils/getEnvVar';

const EXPRESS_API_BASE_URL = getEnvVar('VITE_EXPRESS_API_URL');

export const baseUrl = `${EXPRESS_API_BASE_URL}/api`;

export const apiClient = createClient<paths>({
  baseUrl,
  credentials: 'include', // always send cookies
  headers: { 'Content-Type': 'application/json' }
});

apiClient.use({
  onRequest({ request }) {
    if (typeof window !== 'undefined') {
      request.headers.set('X-Client-URL', window.location.href);
      const previousUrl = getPreviousUrl();
      if (previousUrl) {
        request.headers.set('X-Client-Referrer', previousUrl);
      }
    }
    if (plenaryIdentity.contextId) {
      request.headers.set(PLENARY_CONTEXT_ID_HEADER, plenaryIdentity.contextId);
    }
    return request;
  }
});

/**
 * Error thrown by `apiCall` for non-OK HTTP responses.
 * The body parsed by openapi-fetch is preserved; the HTTP status is attached
 * so callers can branch on it (e.g. show LockedMessage on 403).
 */
export type ApiCallError = {
  status: number;
  [key: string]: unknown;
};

/**
 * Narrowing helper: did this thrown value originate from `apiCall` with the given HTTP status?
 * Lets callers branch on specific HTTP codes (e.g. 403 → LockedMessage) without unsafe casts.
 */
export const isApiErrorStatus = (error: unknown, status: number): error is ApiCallError =>
  error != null && typeof error === 'object' && (error as { status?: unknown }).status === status;

export async function apiCall<T>(
  promise: Promise<{ data?: T | undefined; error?: unknown; response: Response }>
): Promise<T> {
  const { data, error, response } = await promise;
  if (!response.ok) {
    const status = response.status;
    // Wrap primitive/null bodies (e.g. text/plain responses) so `status` always sticks —
    // callers rely on this contract via isApiErrorStatus / QueryClient retry skip.
    const err = (
      error !== null && typeof error === 'object' ? error : new Error(error != null ? String(error) : `HTTP ${status}`)
    ) as Record<string, unknown>;
    err.status = status;
    throw err;
  }
  return data as T;
}
